import { useTranslation } from 'react-i18next';
import { KoobInputDate, KoobInputStepper } from '@koob/ui';
import RoomsCompositionPicker from './RoomsCompositionPicker';

const toDate = value => (value ? (value?.toDate?.() ?? new Date(value)) : null);

export const newGuestsRoomsComposition = () => ({
  adults: 1,
  children: 0,
  childrenBirthdates: [],
  rooms: null,
  showRoomsComposition: false,
});

export const getSingleRoomComposition = ({
  adults,
  children,
  childrenBirthdates,
}) => ({
  adults,
  children,
  childrenBirthdates: childrenBirthdates.slice(0, children),
});

export const getEffectiveComposition = composition =>
  composition.rooms ?? [getSingleRoomComposition(composition)];

/** A hotel search's rooms, one per room of the composition. */
export const toHotelSearchRooms = composition =>
  composition.map(r => ({
    adults: r.adults,
    children: r.children,
    childrenBirthdates: (r.childrenBirthdates || []).slice(0, r.children),
    count: 1,
  }));

export const isGuestsRoomsCompositionValid = ({
  adults,
  children,
  childrenBirthdates,
  rooms,
}) => {
  const allBirthdatesFilled =
    children === 0 || childrenBirthdates.filter(Boolean).length >= children;

  if (!allBirthdatesFilled) return false;
  if (rooms === null) return true;

  const roomsAllocatedAdults = (rooms ?? []).reduce(
    (s, r) => s + (r.adults ?? 0),
    0,
  );
  const roomsAllocatedChildren = (rooms ?? []).reduce(
    (s, r) => s + (r.children ?? 0),
    0,
  );
  const roomsBirthdatesFilled = (rooms ?? []).every(
    r =>
      r.children === 0 ||
      (r.childrenBirthdates || []).slice(0, r.children).filter(Boolean)
        .length >= r.children,
  );
  return (
    roomsAllocatedAdults === adults &&
    roomsAllocatedChildren === children &&
    roomsBirthdatesFilled
  );
};

export default function GuestsRoomsPicker({
  value,
  onChange,
  translationNamespace,
  isVisibleRoom = false,
}) {
  const { t } = useTranslation(translationNamespace);
  const { adults, children, childrenBirthdates, rooms, showRoomsComposition } =
    value;

  const setChildBirthdate = (childIndex, date) => {
    const updated = [...childrenBirthdates];
    updated[childIndex] = date;
    onChange({ ...value, childrenBirthdates: updated });
  };

  const allBirthdatesFilled =
    children === 0 || childrenBirthdates.filter(Boolean).length >= children;

  const handleToggleRooms = () => {
    if (!showRoomsComposition && rooms === null) {
      onChange({
        ...value,
        rooms: [getSingleRoomComposition(value)],
        showRoomsComposition: true,
      });
      return;
    }
    onChange({ ...value, showRoomsComposition: !showRoomsComposition });
  };

  return (
    <div className="space-y-3 border rounded-lg p-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center justify-between border rounded-lg px-4 py-3">
          <div>
            <div className="text-gray-700 font-medium">
              {t('getAvailabilityModal.adults')}
            </div>
            <div className="text-gray-400 text-xs">
              {t('getAvailabilityModal.adultsHint')}
            </div>
          </div>
          <KoobInputStepper
            value={adults}
            min={1}
            onValueChange={newAdults => {
              const trimmedChildrenBirthdates = childrenBirthdates.slice(
                0,
                children,
              );
              onChange({
                ...value,
                adults: newAdults,
                childrenBirthdates: trimmedChildrenBirthdates,
                rooms: [
                  getSingleRoomComposition({
                    adults: newAdults,
                    children,
                    childrenBirthdates: trimmedChildrenBirthdates,
                  }),
                ],
              });
            }}
          />
        </div>

        <div className="flex items-center justify-between border rounded-lg px-4 py-3">
          <div>
            <div className="text-gray-700 font-medium">
              {t('getAvailabilityModal.children')}
            </div>
            <div className="text-gray-400 text-xs">
              {t('getAvailabilityModal.childrenHint')}
            </div>
          </div>
          <KoobInputStepper
            value={children}
            onValueChange={newChildren => {
              const trimmedChildrenBirthdates = childrenBirthdates.slice(
                0,
                newChildren,
              );
              onChange({
                ...value,
                children: newChildren,
                childrenBirthdates: trimmedChildrenBirthdates,
                rooms: [
                  getSingleRoomComposition({
                    adults,
                    children: newChildren,
                    childrenBirthdates: trimmedChildrenBirthdates,
                  }),
                ],
                showRoomsComposition: false,
              });
            }}
          />
        </div>
      </div>

      {children > 0 && (
        <div className="border border-dashed border-gray-200 rounded-xl px-4 py-3 space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t('getAvailabilityModal.childBirthdates')}{' '}
            <span className="text-red-500">*</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: children }).map((_, childIndex) => (
              <div
                key={childIndex}
                className="relative space-y-1"
                style={{ zIndex: children - childIndex }}
              >
                <div className="text-sm font-medium text-gray-700">
                  {t('getAvailabilityModal.child')} {childIndex + 1}
                </div>
                <KoobInputDate
                  block
                  value={toDate(childrenBirthdates?.[childIndex])}
                  onChange={date => setChildBirthdate(childIndex, date)}
                  maxDate={new Date()}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end pt-1">
        <button
          type="button"
          onClick={handleToggleRooms}
          disabled={!allBirthdatesFilled && !showRoomsComposition}
          className="text-orange-500 border border-orange-500 rounded-lg px-4 py-2 text-sm font-medium hover:bg-orange-50 transition-colors bg-transparent outline-none disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {showRoomsComposition
            ? t('getAvailabilityModal.hideRoomComposition')
            : t('getAvailabilityModal.showRoomComposition')}
        </button>
      </div>

      {showRoomsComposition && (
        <RoomsCompositionPicker
          value={rooms ?? [getSingleRoomComposition(value)]}
          onChange={rooms => onChange({ ...value, rooms })}
          maxAdults={adults}
          maxChildren={children}
          parentChildrenBirthdates={childrenBirthdates.slice(0, children)}
          isVisibleRoom={isVisibleRoom}
        />
      )}
    </div>
  );
}
